import type { OpenAIFunction } from '../types/message';
import baseWorkloadApi from './baseWorkloadApi';

// ─── Playground wire types ──────────────────────────────────────────────────
// Shapes the /chatCompletion endpoint actually accepts/returns. The page-level
// types and components import these so request/response stay in sync.

/** Tool definition as the backend expects it (FunctionTool shape). */
export interface PlaygroundFunction {
  type: 'function';
  function: OpenAIFunction;
}

/** A tool call inside a conversation turn or a completion response. */
export interface PlaygroundToolCall {
  id?: string;
  type?: 'function';
  function: { name: string; arguments: string };
}

/** A single conversation message in the shape the chatCompletion endpoint validates. */
export interface PlaygroundConversationMessage {
  role: 'system' | 'user' | 'assistant' | 'function' | 'tool';
  content: string | null;
  name?: string;
  toolCalls?: PlaygroundToolCall[];
  toolCallId?: string;
}

export interface ChatCompletionRequest {
  conversation: PlaygroundConversationMessage[];
  functions: PlaygroundFunction[];
  prompt: string;
  model: string;
  toolChoice?: string;
}

/** Response from /chatCompletion (ChatCompletionResponse + cost/time added by the route). */
export interface ChatCompletionResult {
  completionMessageContent: string | null;
  toolCalls?: PlaygroundToolCall[] | null;
  model: string;
  cost?: string;
  time?: number;
  completionUsage?: unknown;
}

// Playground API Types
export interface ProposedChange {
  description: string;
  textToRemove?: string;
  textToAdd?: string;
}

export interface AnalysisResult {
  analysis:
    | {
        clarity?: string[] | string;
        specificity?: string[] | string;
        structure?: string[] | string;
        suggestions?: string[] | string;
        strengths?: string[] | string;
        potential_issues?: string[] | string;
        raw?: string[] | string;
        analysis?: string[] | string;
      }
    | Record<string, string | string[]>;
  cost: string;
  rawResponse: string;
}

export interface ProposedChangesResult {
  proposedChanges: ProposedChange[];
  cost: string;
  rawResponse: string;
}

export interface BusinessUnitPromptResult {
  /** Interpolated system prompt (placeholders resolved), or the raw template when promptIsFallback. */
  prompt: string;
  /** Stored template with placeholders intact — what an eval run interpolates per case. */
  rawPrompt: string | null;
  tools: { type: 'function'; function: OpenAIFunction }[];
  model: string | null;
  /** True when promptGenerator threw and we returned the raw template instead of the resolved prompt. */
  promptIsFallback: boolean;
}

const playgroundApi = baseWorkloadApi.injectEndpoints({
  endpoints: (build) => ({
    generateRequest: build.mutation<ChatCompletionResult, ChatCompletionRequest>({
      query: (body) => ({
        url: 'admin/playground/chatCompletion',
        method: 'POST',
        body,
      }),
    }),
    analyzePrompt: build.mutation({
      query: (body: { prompt: string }) => ({
        url: 'admin/playground/analyzePrompt',
        method: 'POST',
        body,
      }),
      transformResponse: (response: AnalysisResult): AnalysisResult => response,
    }),
    proposeChanges: build.mutation({
      query: (body: { prompt: string }) => ({
        url: 'admin/playground/proposeChanges',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ProposedChangesResult): ProposedChangesResult => response,
    }),
    businessUnitPrompt: build.query<BusinessUnitPromptResult, { businessUnitId: number }>({
      query: (body) => ({
        url: 'admin/playground/businessUnitPrompt',
        method: 'POST',
        body,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGenerateRequestMutation,
  useAnalyzePromptMutation,
  useProposeChangesMutation,
  useBusinessUnitPromptQuery,
} = playgroundApi;
